import type { OpenedDocument } from './types';

/** Accepted markdown file extensions (lowercase, no dot). */
export const ACCEPTED_EXTENSIONS = ['md', 'markdown', 'mdown', 'mkd'] as const;

/** Maximum file size we will open (10 MB, per FR-7). */
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

/** A user-facing error for the document-loading path (corrupt, too big, etc.). */
export class DocumentLoadError extends Error {
  constructor(
    message: string,
    readonly code: 'too-large' | 'decode-failed' | 'unsupported' | 'read-failed',
  ) {
    super(message);
    this.name = 'DocumentLoadError';
  }
}

/** True if the filename has an accepted markdown extension. */
export function isAcceptedFilename(name: string): boolean {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  return (ACCEPTED_EXTENSIONS as readonly string[]).includes(ext);
}

/** Decode UTF-8 bytes, tolerating a leading BOM. Throws on invalid UTF-8. */
export function decodeUtf8(bytes: Uint8Array): string {
  const text = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
}

/** sha-256 hex digest of the document text (stable content id; future-sync seam). */
export async function hashContent(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Build an {@link OpenedDocument} from raw bytes: enforces the size limit,
 * decodes UTF-8 (BOM-tolerant), and computes the content-hash id.
 */
export async function makeDocument(
  name: string,
  bytes: Uint8Array,
  locator: string | null,
): Promise<OpenedDocument> {
  if (bytes.byteLength > MAX_FILE_BYTES) {
    throw new DocumentLoadError(
      `File is ${(bytes.byteLength / (1024 * 1024)).toFixed(1)} MB; the maximum is 10 MB.`,
      'too-large',
    );
  }
  let text: string;
  try {
    text = decodeUtf8(bytes);
  } catch {
    throw new DocumentLoadError('File is not valid UTF-8 text.', 'decode-failed');
  }
  const id = await hashContent(text);
  return { id, name, text, locator };
}
